import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ApiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { X, ChevronDown, Check, ChevronRight } from "lucide-react";

interface Department {
  id: string;
  name: string;
  color?: string;
  parentId?: string | null;
  children?: Department[];
  level?: number;
  displayName?: string;
  disabled?: boolean;
  hasChildren?: boolean;
}

interface DepartmentSelectorProps {
  value?: string | null;
  onChange: (departmentId: string, department?: Department) => void;
  placeholder?: string;
  excludeIds?: string[];
  className?: string;
  disabled?: boolean;
  required?: boolean;
  showColors?: boolean;
  allowClear?: boolean;
  autoOpen?: boolean;
  inline?: boolean;
  filterByUserResponsibility?: boolean;
  lessonId?: string;
  rootId?: string;
  openDirection?: "down" | "up";
  departmentOptions?: Department[];
}

export default function DepartmentSelector({
  value = null,
  onChange,
  placeholder = "Departman Seçin",
  excludeIds = [],
  className = "",
  disabled = false,
  required = false,
  showColors = true,
  allowClear = true,
  autoOpen = false,
  inline = false,
  filterByUserResponsibility = false,
  lessonId,
  rootId,
  openDirection = "down",
  departmentOptions,
}: DepartmentSelectorProps) {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [responsibleDepartmentIds, setResponsibleDepartmentIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Department[]>([]);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [loadedDepartments, setLoadedDepartments] = useState<Set<string>>(new Set());
  const [cachedSelectedDepartment, setCachedSelectedDepartment] = useState<Department | undefined>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuPortalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);

  const loadDepartmentsData = async (parentId: string | null = null) => {
    try {
      setLoading(true);
      const response = await ApiService.getAllDepartments(parentId);
      const allDeps = response?.data || response || [];
      const activeDepartments = allDeps.filter((d: any) => !d?.deletedAt);

      if (parentId === null) {
        setDepartments(activeDepartments);
      } else {
        setDepartments((prev) => {
          const existingIds = new Set(prev.map((d) => d.id));
          const newDepartments = activeDepartments.filter((d: any) => !existingIds.has(d.id));
          return [...prev, ...newDepartments];
        });
        setLoadedDepartments((prev) => new Set([...prev, parentId]));
      }
    } catch (error) {
      console.error("Departmanlar yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDepartment = async (departmentId: string) => {
    const newExpanded = new Set(expandedDepartments);

    if (newExpanded.has(departmentId)) {
      newExpanded.delete(departmentId);
      const closeChildren = (parentId: string) => {
        departments.forEach((dept) => {
          if (dept.parentId === parentId) {
            newExpanded.delete(dept.id);
            closeChildren(dept.id);
          }
        });
      };
      closeChildren(departmentId);
    } else {
      newExpanded.add(departmentId);
      if (!loadedDepartments.has(departmentId)) {
        await loadDepartmentsData(departmentId);
      }
    }
    setExpandedDepartments(newExpanded);
  };

  const getAllChildIds = (node: Department): string[] => {
    let childIds: string[] = [];
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        childIds.push(child.id);
        childIds = childIds.concat(getAllChildIds(child));
      });
    }
    return childIds;
  };

  const hasResponsibleDescendants = (node: Department, responsibleIds: string[]): boolean => {
    if (responsibleIds.includes(node.id)) return true;
    if (!node.children) return false;
    return node.children.some((child) => hasResponsibleDescendants(child, responsibleIds));
  };

  const canUserSelectDepartment = (department: Department, allDepartments: Department[], responsibleIds: string[]): boolean => {
    if (!filterByUserResponsibility || user?.isSuperAdmin || responsibleIds.includes(department.id)) {
      return true;
    }

    let currentId = department.parentId;
    const visited = new Set<string>();

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      if (responsibleIds.includes(currentId)) return true;

      const parent = allDepartments.find((d) => d.id === currentId);
      currentId = parent?.parentId;
    }

    return false;
  };

  const areAllChildrenSelected = (node: Department, allDepartments: Department[], responsibleIds: string[]): boolean => {
    if (!node.children?.length) return false;

    const getSelectableChildren = (parentNode: Department): Department[] => {
      const children = allDepartments.filter((d) => d.parentId === parentNode.id);
      const selectableChildren: Department[] = [];

      children.forEach((child) => {
        if (canUserSelectDepartment(child, allDepartments, responsibleIds)) {
          selectableChildren.push(child);
        }
        selectableChildren.push(...getSelectableChildren(child));
      });

      return selectableChildren;
    };

    const selectableChildren = getSelectableChildren(node);
    if (selectableChildren.length === 0) return false;

    return selectableChildren.every((child) => excludeIds.includes(child.id));
  };

  useEffect(() => {
    setDepartments([]);
    setLoadedDepartments(new Set());
    setExpandedDepartments(new Set());
  }, [rootId]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      try {
        setLoading(true);

        if (Array.isArray(departmentOptions)) {
          const activeDepartments = departmentOptions.filter((d: any) => !d?.deletedAt);
          const activeIds = new Set(activeDepartments.map((d: any) => String(d.id)));
          const parentIds = new Set(
            activeDepartments
              .map((d: any) => (d.parentId ? String(d.parentId) : null))
              .filter(Boolean) as string[]
          );
          const normalizedDepartments = activeDepartments.map((d: any) => ({
            ...d,
            parentId: d.parentId && !activeIds.has(String(d.parentId)) ? null : d.parentId,
            hasChildren: parentIds.has(String(d.id)),
          }));
          if (!isMounted) return;
          setDepartments(normalizedDepartments);
          setResponsibleDepartmentIds(normalizedDepartments.map((d) => d.id));
          return;
        }

        if (filterByUserResponsibility && lessonId && user?.id) {
          if (user?.isSuperAdmin) {
            await loadDepartmentsData(null);
          } else {
            try {
              const responsibleData = await ApiService.getLessonResponsible(lessonId, user.id);
              if (!isMounted) return;

              if (Array.isArray(responsibleData) && responsibleData.length > 0) {
                const userResponsible = responsibleData[0];
                const responsibleDepts = userResponsible?.departments || [];
                const responsibleDeptIds = responsibleDepts.map((dept: any) => dept.id) || [];

                setResponsibleDepartmentIds(responsibleDeptIds);

                const activeDepartments = responsibleDepts.filter((d: any) => !d?.deletedAt);
                setDepartments(activeDepartments);
              } else {
                setResponsibleDepartmentIds([]);
                setDepartments([]);
              }
            } catch (error) {
              console.error("Ders sorumlulukları yüklenirken hata:", error);
              if (isMounted) {
                setResponsibleDepartmentIds([]);
                setDepartments([]);
              }
            }
          }
        } else {
          if (rootId) {
            const rootResponse = await ApiService.getAllDepartments(null);
            if (!isMounted) return;

            const allRoots = rootResponse?.data || rootResponse || [];
            const targetRoot = allRoots.find((d: any) => d.id === rootId);

            if (targetRoot) {
              const rootWithNullParent = { ...targetRoot, parentId: null };

              setDepartments([rootWithNullParent]);

              setExpandedDepartments(new Set([rootId]));
              setLoadedDepartments(new Set([rootId]));

              const childrenResponse = await ApiService.getAllDepartments(rootId);
              if (!isMounted) return;

              const children = childrenResponse?.data || childrenResponse || [];
              const activeChildren = children.filter((d: any) => !d?.deletedAt);

              setDepartments(prev => {
                const existingIds = new Set(prev.map(d => d.id));
                const uniqueChildren = activeChildren.filter((d: any) => !existingIds.has(d.id));
                return [...prev, ...uniqueChildren];
              });
            }
          } else {
            await loadDepartmentsData(null);
          }
        }
      } catch (error) {
        console.error("Departmanlar yüklenirken hata:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [filterByUserResponsibility, lessonId, user?.id, user?.isSuperAdmin, rootId, departmentOptions]);

  const getAllRelatedDepartments = (departments: Department[], targetIds: string[]): string[] => {
    const relatedIds = new Set<string>();

    const addParentsAndChildren = (deptId: string) => {
      if (relatedIds.has(deptId)) return;
      relatedIds.add(deptId);

      const dept = departments.find((d) => d.id === deptId);
      if (!dept) return;

      if (dept.parentId) addParentsAndChildren(dept.parentId);

      departments.filter((d) => d.parentId === deptId).forEach((child) => addParentsAndChildren(child.id));
    };

    targetIds.forEach(addParentsAndChildren);
    return Array.from(relatedIds);
  };

  const flatDepartments = useMemo(() => {
    if (departments.length === 0) return [];

    let filteredDepartments = departments;

    if (filterByUserResponsibility && !user?.isSuperAdmin) {
      if (responsibleDepartmentIds.length > 0) {
        const responsibleIdsSet = new Set(responsibleDepartmentIds);
        filteredDepartments = departments.map((dept) => {
          if (dept.parentId && !responsibleIdsSet.has(dept.parentId)) {
            return { ...dept, parentId: null };
          }
          return dept;
        });
      } else {
        filteredDepartments = [];
      }
    }

    const getVisibleDepartments = (parentId: string | null = null, level = 0): Department[] => {
      const children = filteredDepartments
        .filter((d) => d.parentId === parentId)
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr", { sensitivity: "accent" }));

      let result: Department[] = [];

      children.forEach((dept) => {
        const isExcluded = excludeIds.includes(dept.id);
        const canUserSelect = canUserSelectDepartment(dept, filteredDepartments, responsibleDepartmentIds);
        const allChildrenSelected = areAllChildrenSelected(dept, filteredDepartments, responsibleDepartmentIds);

        const shouldShowNode = !isExcluded && (canUserSelect || !filterByUserResponsibility) && !allChildrenSelected;

        if (shouldShowNode) {
          result.push({
            ...dept,
            level,
            displayName: `${"  ".repeat(level)}${dept.name}`,
            disabled: filterByUserResponsibility ? !canUserSelect : false,
          });

          if (expandedDepartments.has(dept.id)) {
            result.push(...getVisibleDepartments(dept.id, level + 1));
          }
        }
      });

      return result;
    };

    return getVisibleDepartments(null, 0);
  }, [departments, excludeIds, filterByUserResponsibility, responsibleDepartmentIds, expandedDepartments, user?.isSuperAdmin]);

  const selectedDepartment = useMemo(() => {
    if (!value) return undefined;

    const found =
      flatDepartments.find((d) => d.id === value) ||
      departments.find((d) => d.id === value) ||
      searchResults.find((d) => d.id === value);

    if (found) return found;

    if (cachedSelectedDepartment && cachedSelectedDepartment.id === value) {
      return cachedSelectedDepartment;
    }

    return undefined;
  }, [value, flatDepartments, departments, searchResults, cachedSelectedDepartment]);

  useEffect(() => {
    if (!value) {
      setCachedSelectedDepartment(undefined);
    }
  }, [value]);

  useEffect(() => {
    const searchDepartments = async () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        return;
      }

      if (Array.isArray(departmentOptions)) {
        const normalizedTerm = searchTerm.trim().toLocaleLowerCase("tr");
        const localResults = departmentOptions.filter((dept) =>
          String(dept?.name || "").toLocaleLowerCase("tr").includes(normalizedTerm)
        );
        setSearchResults(localResults);
        return;
      }

      try {
        setLoading(true);
        const response = await ApiService.searchAllDepartments({
          search: searchTerm.trim(),
          sortBy: "name",
          sortOrder: "ASC",
        });
        const results = response?.data || response || [];
        setSearchResults(results);
      } catch (error) {
        console.error("Departman araması başarısız:", error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchDepartments, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, departmentOptions]);

  const filteredDepartments = searchTerm.trim()
    ? searchResults.map((dept) => ({
      ...dept,
      level: 0,
      displayName: dept.name,
      disabled: filterByUserResponsibility ? !canUserSelectDepartment(dept, searchResults, responsibleDepartmentIds) : false,
    }))
    : flatDepartments;

  const updateMenuPosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el || !isOpen) return;

    const rect = el.getBoundingClientRect();
    const margin = 4;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    let width = Math.max(280, rect.width);
    let left = rect.left;
    if (left + width > vw - margin) left = Math.max(margin, vw - width - margin);
    if (left < margin) left = margin;

    if (openDirection === "down") {
      const maxH = Math.min(384, Math.max(120, vh - rect.bottom - margin * 2));
      setMenuStyle({
        position: "fixed",
        top: rect.bottom + margin,
        left,
        width,
        maxHeight: maxH,
        zIndex: 99999,
      });
    } else {
      const maxH = Math.min(384, Math.max(120, rect.top - margin * 2));
      setMenuStyle({
        position: "fixed",
        bottom: vh - rect.top + margin,
        left,
        width,
        maxHeight: maxH,
        zIndex: 99999,
      });
    }
  }, [isOpen, openDirection]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (dropdownRef.current?.contains(t)) return;
      if (menuPortalRef.current?.contains(t)) return;
      setIsOpen(false);
      setSearchTerm("");
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (departmentId: string) => {
    let selectedDept = filteredDepartments.find((d) => d.id === departmentId);

    if (!selectedDept) {
      selectedDept = departments.find((d) => d.id === departmentId);
    }

    if (!selectedDept) {
      selectedDept = searchResults.find((d) => d.id === departmentId);

      if (selectedDept && !departments.find((d) => d.id === departmentId)) {
        setDepartments((prev) => [...prev, selectedDept as Department]);
      }
    }

    setCachedSelectedDepartment(selectedDept);

    onChange(departmentId, selectedDept);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = () => {
    if (allowClear && !required) {
      setCachedSelectedDepartment(undefined);
      onChange("", undefined);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  if (inline) {
    return (
      <div className={`${className}`} ref={dropdownRef}>
        <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <input
              ref={inputRef}
              type="text"
              placeholder="Departman ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                <span className="text-xs mt-1 block">Yükleniyor...</span>
              </div>
            ) : filteredDepartments.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {searchTerm
                  ? "Sonuç Bulunamadı"
                  : filterByUserResponsibility && !user?.isSuperAdmin
                    ? "Departman Sorumluluğunuz Bulunmuyor"
                    : "Departman Bulunamadı"}
              </div>
            ) : (
              <>
                {allowClear && !required && (
                  <button
                    type="button"
                    onClick={() => handleSelect("")}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100">
                    <span className="text-gray-400">Seçim Yok</span>
                  </button>
                )}
                {filteredDepartments.map((department) => (
                  <div key={department.id} className="flex items-center">
                    {!searchTerm.trim() && department.hasChildren ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDepartment(department.id);
                        }}
                        className="p-2 hover:bg-gray-100 rounded flex-shrink-0"
                        style={{ marginLeft: `${(department.level || 0) * 16}px` }}>
                        {expandedDepartments.has(department.id) ? (
                          <ChevronDown className="w-3 h-3 text-gray-600" />
                        ) : (
                          <ChevronRight className="w-3 h-3 text-gray-600" />
                        )}
                      </button>
                    ) : (
                      <span
                        className={searchTerm.trim() ? "" : "w-7 flex-shrink-0"}
                        style={{ marginLeft: searchTerm.trim() ? "0px" : `${(department.level || 0) * 16}px` }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => !department.disabled && handleSelect(department.id)}
                      disabled={department.disabled}
                      className={`
                        flex-1 px-4 py-2 text-left text-sm flex items-center gap-2
                        ${department.disabled ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-50"}
                        ${value === department.id ? "bg-blue-50 text-blue-600" : department.disabled ? "text-gray-400" : "text-gray-900"}
                      `}>
                      {showColors && department.color && (
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: department.color }} />
                      )}
                      <span className="truncate">{department.name}</span>
                      {department.disabled && <span className="text-xs text-gray-400 ml-auto">(Sadece Alt Departmanlar)</span>}
                      {value === department.id && !department.disabled && <Check className="w-4 h-4 ml-auto flex-shrink-0" />}
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const menuPortalContent =
    isOpen && menuStyle ? (
      <div
        ref={menuPortalRef}
        className="overflow-y-auto overscroll-contain rounded-lg border border-gray-300 bg-white shadow-lg"
        style={menuStyle}>
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white p-2">
          <input
            ref={inputRef}
            type="text"
            placeholder="Departman ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            autoFocus
          />
        </div>

        {loading ? (
          <div className="p-4 text-center text-gray-500">
            <div className="mx-auto h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <span className="mt-1 block text-xs">Yükleniyor...</span>
          </div>
        ) : filteredDepartments.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {searchTerm
              ? "Sonuç Bulunamadı"
              : filterByUserResponsibility && !user?.isSuperAdmin
                ? "Departman Sorumluluğunuz Bulunmuyor"
                : "Departman Bulunamadı"}
          </div>
        ) : (
          <>
            {allowClear && !required && (
              <button
                type="button"
                onClick={() => handleSelect("")}
                className="flex w-full items-center gap-2 border-b border-gray-100 px-4 py-2 text-left text-sm hover:bg-gray-50">
                <span className="text-gray-400">Tüm Departmanlar</span>
              </button>
            )}
            {filteredDepartments.map((department) => (
              <div key={department.id} className="flex items-center">
                {!searchTerm.trim() && department.hasChildren ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDepartment(department.id);
                    }}
                    className="flex-shrink-0 rounded p-2 hover:bg-gray-100"
                    style={{ marginLeft: `${(department.level || 0) * 16}px` }}>
                    {expandedDepartments.has(department.id) ? (
                      <ChevronDown className="h-3 w-3 text-gray-600" />
                    ) : (
                      <ChevronRight className="h-3 w-3 text-gray-600" />
                    )}
                  </button>
                ) : (
                  <span
                    className={searchTerm.trim() ? "" : "w-7 flex-shrink-0"}
                    style={{ marginLeft: searchTerm.trim() ? "0px" : `${(department.level || 0) * 16}px` }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => !department.disabled && handleSelect(department.id)}
                  disabled={department.disabled}
                  className={`
                        flex flex-1 items-center gap-2 px-4 py-2 text-left text-sm
                        ${department.disabled ? "cursor-not-allowed text-gray-400" : "hover:bg-gray-50"}
                        ${value === department.id ? "bg-blue-50 text-blue-600" : department.disabled ? "text-gray-400" : "text-gray-900"}
                      `}>
                  {showColors && department.color && (
                    <div className="h-3 w-3 flex-shrink-0 rounded-full" style={{ backgroundColor: department.color }} />
                  )}
                  <span className="truncate">{department.name}</span>
                  {department.disabled && <span className="ml-auto text-xs text-gray-400">(Sadece Alt Departmanlar)</span>}
                  {value === department.id && !department.disabled && <Check className="ml-auto h-4 w-4 flex-shrink-0" />}
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    ) : null;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-4 py-2 text-left border rounded-lg focus:ring-2 focus:ring-blue-500
          transition-colors duration-200 flex items-center justify-between
          ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200" : "bg-white hover:border-gray-400 border-gray-300"}
          ${isOpen ? "ring-2 ring-blue-500 border-blue-500" : ""}
        `}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedDepartment ? (
            <>
              {showColors && selectedDepartment.color && (
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: selectedDepartment.color }} />
              )}
              <span className="truncate text-gray-900">{selectedDepartment.displayName || selectedDepartment.name}</span>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {selectedDepartment && allowClear && !required && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                }
              }}>
              <X className="w-3 h-3 text-gray-400" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>

      {typeof document !== "undefined" && menuPortalContent ? createPortal(menuPortalContent, document.body) : null}
    </div>
  );
}
